/**
 * api/index.ts — assembles all modules into the unified `api` object.
 * Import from 'src/lib/api' (via the barrel api.ts → api/index.ts).
 */
export { fetchApi, fetchPatientApi, API_BASE_URL, ApiError } from './client';
export type { ApiResponse } from './client';

import { authModule }                        from './modules/auth';
import { billingModule }                     from './modules/billing';
import { patientsModule }                    from './modules/patients';
import { appointmentsModule }                from './modules/appointments';
import {
  visitsModule, notesModule, vitalsModule,
  definitionsModule, readingsModule, labUploadModule,
} from './modules/clinical';
import { prescriptionsModule, pharmaciesModule } from './modules/prescriptions';
import {
  pharmacyPortalModule, pharmacyAdminModule, pharmacyMedicationsModule,
} from './modules/pharmacy';
import {
  clinicsModule, clinicAccessModule, usersModule, doctorsModule,
  staffModule, auditModule, errorLogsModule, analyticsModule,
  securityModule, inventoryModule, profileModule, aiModule,
  reportsModule, queueModule, scheduleModule, schedulesModule, doctorStatusModule,
} from './modules/admin';
import {
  feeScheduleModule, labTestModule, procedureModule,
  medicineItemModule, samplesModule,
} from './modules/fee-config';
import { inpatientModule }                   from './modules/inpatient';
import { departmentsModule, encounterTemplatesModule, obstetricModule, pediatricModule } from './modules/specialty';
import {
  servicesModule, billItemsModule, publicModule,
  notificationsModule, messagesModule, sessionsModule, patientMessagesModule,
  commonChronicConditionsModule, commonAllergiesModule, followUpModule, labsModule,
} from './modules/misc';
import {
  patientPortalModule, templatesModule, favoritesModule,
  templateEnhancementsModule, diagnosesModule, createEnhancedPrescription,
} from './modules/patient-portal';
import { engagementModule } from './modules/engagement';
import { growthModule } from './modules/growth';
import { paymentsModule } from './modules/payments';
import { healthPackagesModule } from './modules/healthpackages';
import { referralsModule } from './modules/referrals';
import { teleconsultModule } from './modules/teleconsult';
import { corporateModule } from './modules/corporate';
import { revenueIntelligenceModule } from './modules/revenueIntelligence';
import { checkinModule } from './modules/checkin';
import { feedbackModule } from './modules/feedback';
import { chronicDiseaseModule } from './modules/chronicDisease';
import { testDataModule } from './modules/testData';
import { erasureModule } from './modules/erasure';
import { mfaModule } from './modules/mfa';
import { labAccessionModule } from './modules/labAccession';
import { labCriticalAlertsModule } from './modules/labCriticalAlerts';
import { labInventoryModule } from './modules/labInventory';
import { labQcModule } from './modules/labQc';
import { clinicalReferralsModule } from './modules/clinicalReferrals';
import { clinicalProceduresModule } from './modules/clinicalProcedures';
import { externalReferralPortalModule } from './modules/externalReferralPortal';
import { clinicalTransfersModule } from './modules/clinicalTransfers';
import { mcpDraftNotesModule, mcpAuditModule, mcpSettingsModule } from './modules/mcp';

// ── Top-level helpers (previously loose functions on `api`) ───────────────
import { fetchApi } from './client';
import type { Pharmacy } from '../../types';

const pharmacyHelpers = {
  getPharmacies: async () => {
    const res = await fetchApi<Pharmacy[]>('/api/pharmacies');
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  createPharmacyUser:        pharmacyAdminModule.createPharmacyUser,
  getPendingPharmacyInvitations: async () => {
    const res = await pharmacyAdminModule.getPendingInvitations();
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  resendPharmacyInvitation:  pharmacyAdminModule.resendInvitation,
  cancelPharmacyInvitation:  pharmacyAdminModule.cancelInvitation,
  registerPharmacy:          pharmacyAdminModule.registerPharmacy,
  acceptPharmacyInvitation:  pharmacyAdminModule.acceptInvitation,
  invitePharmacy:            pharmacyAdminModule.invitePharmacy,
  getPendingPharmacyApprovals: async () => {
    const res = await pharmacyAdminModule.getPendingApprovals();
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  approvePharmacy:           pharmacyAdminModule.approve,
  rejectPharmacy:            pharmacyAdminModule.reject,
  suspendPharmacy:           pharmacyAdminModule.suspend,
  reactivatePharmacy:        pharmacyAdminModule.reactivate,
  getPharmacyStatistics: async () => {
    const res = await pharmacyAdminModule.getStatistics();
    if (res.error) throw new Error(res.error);
    return res.data || null;
  },
  searchPharmacies:          pharmacyAdminModule.search,
  resendPharmacyActivation:  pharmacyAdminModule.resendActivation,
  sendInitialPharmacyActivation: pharmacyAdminModule.sendInitialActivation,
  deactivatePharmacy:        pharmacyAdminModule.deactivate,
  createInHousePharmacy:     pharmacyAdminModule.createInHousePharmacy,
  getClinicInHousePharmacies: pharmacyAdminModule.getClinicInHousePharmacies,
  getClinicActivePharmacies: pharmacyAdminModule.getClinicActivePharmacies,
  getInHousePharmacy:        pharmacyAdminModule.getInHousePharmacy,
  updateInHousePharmacy:     pharmacyAdminModule.updateInHousePharmacy,
  deactivateInHousePharmacy: pharmacyAdminModule.deactivateInHousePharmacy,
  invitePharmacyStaff:       pharmacyAdminModule.invitePharmacyStaff,
};

const clinicHelpers = {
  createClinicWithAdmin: clinicsModule.createWithAdmin,
  getClinics:            clinicsModule.getClinics,
  resendClinicInvitation: clinicsModule.resendInvitation,
  deactivateClinic:      clinicsModule.deactivate,
  activateClinic:        clinicsModule.activate,
};

// ── Main api object ────────────────────────────────────────────────────────

export const api = {
  // ── Auth ─────────────────────────────────────────────────────────────────
  auth: authModule,

  // ── Core clinical ─────────────────────────────────────────────────────────
  patients:     patientsModule,
  appointments: appointmentsModule,
  visits:       visitsModule,
  notes:        notesModule,
  vitals:       vitalsModule,
  definitions:  definitionsModule,
  readings:     readingsModule,
  labUpload:    labUploadModule,

  // ── Prescriptions ─────────────────────────────────────────────────────────
  prescriptions: prescriptionsModule,
  pharmacies:    pharmaciesModule,

  // ── Billing ───────────────────────────────────────────────────────────────
  billing: billingModule,
  bills:   billingModule,  // legacy alias

  // ── Pharmacy platform ─────────────────────────────────────────────────────
  pharmacy:             pharmacyPortalModule,
  pharmacyMedications:  pharmacyMedicationsModule,

  // ── Admin / settings ──────────────────────────────────────────────────────
  clinics:      clinicsModule,
  clinicAccess: clinicAccessModule,
  users:        usersModule,
  doctors:      doctorsModule,
  staff:        staffModule,
  audit:        auditModule,
  errorLogs:    errorLogsModule,
  analytics:    analyticsModule,
  security:     securityModule,
  inventory:    inventoryModule,
  profile:      profileModule,
  ai:           aiModule,
  adminAiReview: adminAiReviewModule,
  navRoutes:     navRoutesModule,
  doctorDashboard: doctorDashboardModule,
  consent:         consentModule,
  aiLabSummary,
  aiClinicalSummary,
  aiPatientHistory,
  aiBillingAssistant,
  aiStaffChat,
  clinicAiSettings,
  reports:      reportsModule,

  // ── Scheduling ───────────────────────────────────────────────────────────
  queue:        queueModule,
  schedule:     scheduleModule,
  schedules:    schedulesModule,
  doctorStatus: doctorStatusModule,

  // ── Fee configuration ────────────────────────────────────────────────────
  feeSchedule:  feeScheduleModule,
  labTest:      labTestModule,
  procedure:    procedureModule,
  medicineItem: medicineItemModule,
  pharmacyItem: medicineItemModule,  // alias
  samples:      samplesModule,

  // ── Inpatient ─────────────────────────────────────────────────────────────
  inpatient: inpatientModule,

  // ── Lab accession workflow ────────────────────────────────────────────────
  labAccessions:    labAccessionModule,
  labCriticalAlerts: labCriticalAlertsModule,
  labInventory:      labInventoryModule,
  labQc:             labQcModule,

  // ── Misc ─────────────────────────────────────────────────────────────────
  services:               servicesModule,
  billItems:              billItemsModule,
  public:                 publicModule,
  notifications:          notificationsModule,
  messages:               messagesModule,
  patientMessages:        patientMessagesModule,
  sessions:               sessionsModule,
  commonChronicConditions: commonChronicConditionsModule,
  commonAllergies:        commonAllergiesModule,
  followUp:               followUpModule,
  followUps:              followUpModule,  // alias
  labs:                   labsModule,
  departments:            departmentsModule,
  encounterTemplates:     encounterTemplatesModule,
  obstetric:              obstetricModule,
  pediatric:              pediatricModule,
  engagement:             engagementModule,
  growth:                 growthModule,
  payments:               paymentsModule,
  healthPackages:         healthPackagesModule,
  referrals:              referralsModule,
  clinicalReferrals:      clinicalReferralsModule,
  // Clinical procedure records. `procedure` above is the CATALOGUE / fee schedule — a different feature.
  clinicalProcedures:     clinicalProceduresModule,
  externalReferralPortal: externalReferralPortalModule,
  clinicalTransfers:      clinicalTransfersModule,
  teleconsult:            teleconsultModule,
  corporate:              corporateModule,
  revenueIntelligence:    revenueIntelligenceModule,
  checkin:                checkinModule,
  feedback:               feedbackModule,
  chronicDisease:         chronicDiseaseModule,
  testData:               testDataModule,
  erasure:                erasureModule,
  mfa:                    mfaModule,
  mcpDraftNotes:          mcpDraftNotesModule,
  mcpAudit:               mcpAuditModule,
  mcpSettings:            mcpSettingsModule,

  // ── Loose top-level helpers (pharmacy & clinic lifecycle) ─────────────────
  ...clinicHelpers,
  ...pharmacyHelpers,
} as const;

export default api;

// ── Named exports for modules used directly (not via api.x) ──────────────
export const patientApi = patientPortalModule;
export const templates            = templatesModule;
export const templateEnhancements = templateEnhancementsModule;
export const favorites            = favoritesModule;
export const diagnoses  = diagnosesModule;
export { createEnhancedPrescription };
export { inpatientModule as inpatient };
import { prescriptionSafety as prescriptionSafetyModule } from './modules/prescriptionSafety';
export const prescriptionSafety = prescriptionSafetyModule;
import { adminAiReviewModule } from './modules/adminAiReview';
export const adminAiReview = adminAiReviewModule;
import { navRoutesModule } from './modules/navRoutes';
export const navRoutes = navRoutesModule;
export type { NavRouteItem, NavVisibilityRow } from './modules/navRoutes';
import { doctorDashboardModule } from './modules/doctorDashboard';
export const doctorDashboard = doctorDashboardModule;
import { consentModule } from './modules/consent';
export const consent = consentModule;
import {
  aiLabSummary, aiClinicalSummary, aiPatientHistory,
  aiBillingAssistant, aiStaffChat, clinicAiSettings, aiDiagnosisTemplates,
} from './modules/aiFeatures';
export {
  aiLabSummary, aiClinicalSummary, aiPatientHistory,
  aiBillingAssistant, aiStaffChat, clinicAiSettings, aiDiagnosisTemplates,
} from './modules/aiFeatures';
export type {
  LabSummaryResult, ClinicalSummaryResult, PatientHistoryResult,
  BillingAssistantResult, AiChatMessage, ChatResponse, AiFeatureConfig,
  MissingCharge, PatientHistorySummary, AiApprovalStatus,
  DiagnosisMedicationLine, DiagnosisSuggestionResult, DiagnosisApprovePayload,
} from './modules/aiFeatures';
export type {
  PrescriptionSafetyResponse,
  SafetyFinding,
  AiRiskLevel,
  AiRequestStatus,
  AiSafetyApproval,
} from './modules/prescriptionSafety';
